import React, { useEffect, useRef } from "react";
import { Control, SetFieldValue, UseFormWatch } from "react-hook-form";

import { Box } from "@mui/material";

import {
  useAnalytics,
  useAuth,
  useCurrentUser,
  useDefaultCountry,
  useOperator,
  useScreenSize,
} from "../../../../../../globals/hooks";
import RHFInternationalPhoneInput from "../../../../../react-hook-form/RHFInternationalPhoneInput";
import { RHFTextField } from "../../../../../react-hook-form/text-field/RHFTextField";
import authApi from "utils/auth/authApi";
import { getIsNetflixLogin } from "utils/auth/isNetflixLogin";
import { useIsWhiteLabel } from "pages/new/hooks";

type BookingContactFormProps = {
  control: Control<any>;
  watch: UseFormWatch<any>;
  trackingFrom?: "reserve" | "quoteRequest";
  setValue: SetFieldValue<any>;
  getValues: any;
};

const AUTO_LOGIN_COOLDOWN_MS = 60 * 1000;

type AutoLoginAttemptState = {
  inFlight: boolean;
  lastAttemptedValue: string | null;
  blockedUntil: number;
};

const createDefaultAttemptState = (): AutoLoginAttemptState => ({
  inFlight: false,
  lastAttemptedValue: null,
  blockedUntil: 0,
});

const shouldSkipAutoLoginAttempt = (
  state: AutoLoginAttemptState,
  currentValue: string,
) => {
  const now = Date.now();

  return (
    state.inFlight ||
    (state.lastAttemptedValue === currentValue && now < state.blockedUntil)
  );
};

function BookingContactForm(props: BookingContactFormProps) {
  const { control, trackingFrom, setValue, getValues } = props;
  const isNetflixLogin = getIsNetflixLogin();

  // hooks
  const { onLoginDialogOpen } = useAuth();
  const currentUser = useCurrentUser();
  const defaultCountry = useDefaultCountry();
  const { operator } = useOperator();
  const { isMobileView } = useScreenSize();
  const isDisabledContactField = isNetflixLogin;
  const { isWhiteLabel } = useIsWhiteLabel();

  const { track } = useAnalytics();
  const phoneAutoLoginRef = useRef<AutoLoginAttemptState>(
    createDefaultAttemptState(),
  );
  const emailAutoLoginRef = useRef<AutoLoginAttemptState>(
    createDefaultAttemptState(),
  );

  useEffect(() => {
    const bookingContact = getValues("bookingContact");

    if (
      isNetflixLogin &&
      currentUser?.email &&
      currentUser?.firstName &&
      currentUser?.lastName &&
      !bookingContact?.email?.length
    ) {
      setValue("bookingContact.email", currentUser.email);
      setValue("bookingContact.firstName", currentUser.firstName);
      setValue("bookingContact.lastName", currentUser.lastName);
    }
  }, [
    currentUser?.email,
    currentUser?.firstName,
    currentUser?.lastName,
    getValues,
    setValue,
    isNetflixLogin,
  ]);

  const onPhoneInputFocus = () => {
    if (trackingFrom) {
      track(`${trackingFrom}_phoneSelected`);
    }
  };

  const onEmailInputFocus = () => {
    if (trackingFrom) {
      track(`${trackingFrom}_emailSelected`);
    }
  };

  const onFirstNameInputFocus = () => {
    if (trackingFrom) {
      track(`${trackingFrom}_firstNameSelected`);
    }
  };

  const onLastNameInputFocus = () => {
    if (trackingFrom) {
      track(`${trackingFrom}_lastNameSelected`);
    }
  };

  const findExistingUserWithMobileInput = async () => {
    const bookingContact = getValues("bookingContact");
    const phoneFields = bookingContact?.phoneFields;
    const mobilePhone = phoneFields?.mobilePhone?.trim();
    const attemptState = phoneAutoLoginRef.current;

    if (isWhiteLabel || !mobilePhone || mobilePhone.length < 8) return;
    if (shouldSkipAutoLoginAttempt(attemptState, mobilePhone)) return;

    attemptState.inFlight = true;

    try {
      const { status } = await authApi.sendCode({
        mobilePhone: phoneFields,
        email: null,
        operatorSlug: operator.nameSlug,
      });

      attemptState.lastAttemptedValue = mobilePhone;
      attemptState.blockedUntil = Date.now() + AUTO_LOGIN_COOLDOWN_MS;

      if (status === 200) {
        onLoginDialogOpen({
          mobilePhone: phoneFields,
          verifyCodeText: "We found an account with your mobile number",
          verifyButtonText: "Login",
        });
      }
    } finally {
      attemptState.inFlight = false;
    }
  };

  const findExistingUserWithEmailInput = async () => {
    const bookingContact = getValues("bookingContact");
    const email = bookingContact?.email?.trim().toLowerCase();
    const attemptState = emailAutoLoginRef.current;

    if (isWhiteLabel || !email) return;
    if (shouldSkipAutoLoginAttempt(attemptState, email)) return;

    attemptState.inFlight = true;

    try {
      const { status } = await authApi.sendCode({
        email,
        mobilePhone: null,
        operatorSlug: operator.nameSlug,
      });

      attemptState.lastAttemptedValue = email;
      attemptState.blockedUntil = Date.now() + AUTO_LOGIN_COOLDOWN_MS;

      if (status === 200) {
        onLoginDialogOpen({
          email,
          verifyCodeText: "We found an account with your email",
          verifyButtonText: "Login",
        });
      }
    } finally {
      attemptState.inFlight = false;
    }
  };

  return (
    <Box display="flex" flexDirection="column" mt={3} id="booking-contact">
      {/* mobile phone */}
      <Box mb={1}>
        <RHFInternationalPhoneInput
          name="bookingContact.phoneFields"
          control={control}
          onBlur={findExistingUserWithMobileInput}
          onFocus={onPhoneInputFocus}
          defaultCountry={defaultCountry}
        />
      </Box>
      {/* email */}
      <Box mb={1}>
        <RHFTextField
          required
          fullWidth
          label="Email"
          type="email"
          name="bookingContact.email"
          control={control}
          disabled={isDisabledContactField}
          onBlur={findExistingUserWithEmailInput}
          onFocus={onEmailInputFocus}
        />
      </Box>
      <Box
        display="flex"
        flexDirection={isMobileView ? "column" : "row"}
        mb={1}
      >
        {/* first name */}
        <Box
          mb={isMobileView ? 1 : undefined}
          mr={!isMobileView ? 1 : undefined}
          flex="1"
        >
          <RHFTextField
            required
            fullWidth
            label="First name"
            type="text"
            name="bookingContact.firstName"
            control={control}
            disabled={isDisabledContactField}
            onFocus={onFirstNameInputFocus}
          />
        </Box>

        {/* last name */}
        <Box flex="1">
          <RHFTextField
            required
            fullWidth
            label="Last name"
            type="text"
            name="bookingContact.lastName"
            control={control}
            disabled={isDisabledContactField}
            onFocus={onLastNameInputFocus}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default BookingContactForm;
